import api from "./api";
import { parsePaginatedList } from "../utils/pagination";

export interface ProductSalesPointItem {
  id: string;
  productSalesPointId: string;
  productMasterId: string;
  salesPointId: string;
  name: string;
  sku: string;
  barcode: string | null;
  itemCode?: string | null;
  hsnCode?: string | null;
  categoryId: string;
  unit: string;
  productType: string;
  basePrice: number;
  purchasePrice: number;
  sellingPrice: number;
  wholesalePrice?: number | null;
  mrp?: number | null;
  discountType: "PERCENT" | "FLAT" | null;
  discountValue: number | null;
  gstPercentage: number;
  isTaxInclusive: boolean;
  pricingMode: "QUANTITY_BASED" | "AMOUNT_BASED";
  pricePerUnit: number | null;
  allowFractionalQty?: boolean;
  availability?: string;
  status: string;
  hideFromBilling?: boolean;
  category?: { id: string; name: string };
  activeScheme?: {
    id: string;
    name: string;
    schemePrice: number;
    startsAt: string;
    endsAt: string;
    isActive: boolean;
  } | null;
  effectiveSellingPrice?: number;
}

export interface ProductPriceScheme {
  id: string;
  productSalesPointId: string;
  name: string;
  schemePrice: number;
  startsAt: string;
  endsAt: string;
  isActive: boolean;
}

export interface ProductPriceSchemeInput {
  name: string;
  schemePrice: number;
  startsAt: string;
  endsAt: string;
  isActive?: boolean;
}

export interface ProductSalesPointUpdateData {
  name?: string;
  barcode?: string | null;
  sku?: string;
  itemCode?: string | null;
  hsnCode?: string | null;
  unit?: string;
  categoryId?: string | null;
  basePrice?: number;
  purchasePrice?: number;
  sellingPrice?: number;
  wholesalePrice?: number | null;
  mrp?: number | null;
  discountType?: "PERCENT" | "FLAT" | null;
  discountValue?: number | null;
  gstPercentage?: number;
  isTaxInclusive?: boolean;
  pricingMode?: "QUANTITY_BASED" | "AMOUNT_BASED";
  pricePerUnit?: number | null;
  allowFractionalQty?: boolean;
  availability?: "DINE_IN" | "TAKEAWAY" | "DELIVERY" | "ALL";
  status?: "ACTIVE" | "INACTIVE";
  hideFromBilling?: boolean;
}

export interface ProductMasterSearchItem {
  id: string;
  name: string;
  sku: string;
  barcode: string | null;
  sellingPrice: number;
  category?: { id: string; name: string };
}

export interface ProductSalesPointListParams {
  pageNo?: number;
  pageSize?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: "ASC" | "DESC";
}

export const productSalesPointAPI = {
  listBySalesPoint: async (
    salesPointId: string,
    params?: ProductSalesPointListParams
  ): Promise<{ data: ProductSalesPointItem[]; total: number }> => {
    const response = await api.get(`/product-sales-points/salesPoint/${salesPointId}`, {
      params,
    });
    const payload = response.data.data;
    if (payload && typeof payload === "object" && Array.isArray(payload.items)) {
      const { data, total } = parsePaginatedList<ProductSalesPointItem>(payload);
      return { data, total };
    }
    if (Array.isArray(payload)) {
      return { data: payload, total: payload.length };
    }
    return { data: [], total: 0 };
  },

  searchMaster: async (
    salesPointId: string,
    q: string,
    limit = 100,
    workCategory?: string
  ): Promise<ProductMasterSearchItem[]> => {
    const response = await api.get("/product-sales-points/master/search", {
      params: {
        salesPointId,
        q,
        limit,
        ...(workCategory && workCategory !== "all"
          ? { workCategory }
          : {}),
      },
    });
    return response.data.data ?? [];
  },

  createDirect: async (data: {
    salesPointId: string;
    name: string;
    sellingPrice: number;
    unit?: string;
    barcode?: string | null;
    categoryId?: string | null;
    pricingMode?: "QUANTITY_BASED" | "AMOUNT_BASED";
    allowFractionalQty?: boolean;
    gstPercentage?: number;
    isTaxInclusive?: boolean;
    basePrice?: number;
    purchasePrice?: number;
    pricePerUnit?: number | null;
  }): Promise<ProductSalesPointItem> => {
    const response = await api.post("/product-sales-points/create-direct", data);
    return response.data.data;
  },

  importProduct: async (
    productMasterId: string,
    salesPointId: string
  ): Promise<ProductSalesPointItem> => {
    const response = await api.post("/product-sales-points/import", {
      productMasterId,
      salesPointId,
    });
    return response.data.data;
  },

  importProductsBulk: async (
    salesPointId: string,
    options: { productMasterIds?: string[]; workCategory?: string }
  ): Promise<{ importedCount: number; skippedCount: number }> => {
    const response = await api.post("/product-sales-points/import/bulk", {
      salesPointId,
      ...(options.productMasterIds?.length
        ? { productMasterIds: options.productMasterIds }
        : {}),
      ...(options.workCategory ? { workCategory: options.workCategory } : {}),
    });
    const data = response.data.data ?? {};
    return {
      importedCount: data.importedCount ?? 0,
      skippedCount: data.skippedCount ?? 0,
    };
  },

  update: async (
    id: string,
    data: ProductSalesPointUpdateData
  ): Promise<ProductSalesPointItem> => {
    const response = await api.put(`/product-sales-points/${id}`, data);
    return response.data.data;
  },

  remove: async (id: string): Promise<void> => {
    await api.delete(`/product-sales-points/${id}`);
  },

  listSchemes: async (
    productSalesPointId: string
  ): Promise<ProductPriceScheme[]> => {
    const response = await api.get(
      `/product-sales-points/${productSalesPointId}/schemes`
    );
    return response.data.data ?? [];
  },

  createScheme: async (
    productSalesPointId: string,
    data: ProductPriceSchemeInput
  ): Promise<ProductPriceScheme> => {
    const response = await api.post(
      `/product-sales-points/${productSalesPointId}/schemes`,
      data
    );
    return response.data.data;
  },

  deleteScheme: async (schemeId: string): Promise<void> => {
    await api.delete(`/product-sales-points/schemes/${schemeId}`);
  },
};
